/**
 * BrandThemeLoader — aplica los colores de la Marca a toda la app en runtime.
 *
 * Vive en la capa `app` (puede importar features). Reacciona a la sesión:
 *  - autenticado  → trae la Marca una vez y la aplica (re-skin global).
 *  - no autenticado / logout → resetea a la paleta NEUTRA.
 *
 * La marca es cosmética: cualquier error al traerla se ignora en silencio y la
 * app queda neutra. Nunca bloquea ni muestra error.
 *
 * No renderiza nada (efecto puro sobre :root).
 */
import { useEffect } from "react";
import { useAuth } from "@/features/auth/index.js";
import { getMarca } from "@/features/admin-panel/services/marca.service.js";
import { applyBrandTheme } from "@/shared/lib/brand-theme.js";
import { setBrandLogoUrl } from "@/shared/lib/brand-logo.js";

export function BrandThemeLoader(): null {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      // Logout o sesión ausente → paleta neutra y sin logo de marca.
      applyBrandTheme(null);
      setBrandLogoUrl(null);
      return;
    }

    let vivo = true;
    getMarca()
      .then((marca) => {
        if (!vivo) return;
        applyBrandTheme(marca);
        setBrandLogoUrl(marca.logoUrl.trim() || null);
      })
      .catch(() => {
        // Silencioso: la marca es cosmética, nunca bloquea la app.
        if (!vivo) return;
        applyBrandTheme(null);
        setBrandLogoUrl(null);
      });

    return () => {
      vivo = false;
    };
  }, [isAuthenticated]);

  return null;
}
